/**
 * サーバとの接続。入場券・再接続・追いつき・未送信の送り直しを引き受ける。
 *
 * WebSocket にはクッキーを載せられないので、**寿命 60 秒の入場券**を通常の要求で受け取ってから
 * 繋ぐ。券は利用者とワークスペースに縛られていて、他所では使えない。繋ぎ直すたびに取り直す。
 */

import type { SyncOp } from "@biside/domain";
import type { OutlineStore } from "./store.ts";

export type LinkState = "connecting" | "live" | "down";

export class SyncLink {
  private ws: WebSocket | null = null;
  private backoff = 500;
  private closed = false;

  /** 今どこに誰が居るか。行 id → 人。届いた順に上書きする。 */
  readonly presence = new Map<string, string>();
  onPresence: (() => void) | null = null;

  constructor(
    private readonly workspaceId: string,
    private readonly store: OutlineStore,
    private readonly onState: (state: LinkState, detail: string) => void,
  ) {}

  /**
   * 自分の居場所を知らせる。**記録は残らない**（サーバは今つながっている人へ配るだけ）。
   * 見ているだけで履歴が膨らむのを避けるため、操作の流れとは別の道を通す。
   */
  tellPresence(nodeId: string | null): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "presence", at: nodeId }));
    }
  }

  async start(): Promise<void> {
    const snap = await fetch(`/api/workspaces/${this.workspaceId}/snapshot`, { credentials: "same-origin" });
    if (snap.ok) {
      const json = (await snap.json()) as { nodes: any[]; seq: number };
      this.store.reset(json.nodes, json.seq);
    }
    this.connect();
  }

  stop(): void {
    this.closed = true;
    this.ws?.close();
  }

  send(ops: SyncOp[]): void {
    // 手元への適用と未送信の記録は store が済ませている。繋がっていなければ送らずに溜める。
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: "ops", ops }));
    else this.onState("down", `未送信 ${this.store.pendingCount} 件`);
  }

  private async connect(): Promise<void> {
    if (this.closed) return;
    this.onState("connecting", "接続しています");

    const res = await fetch(`/api/workspaces/${this.workspaceId}/ticket`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    if (!res.ok) return this.retry("入場券を受け取れません");

    const { ticket } = (await res.json()) as { ticket: string };
    const url = new URL(`/ws/${this.workspaceId}`, location.href);
    url.protocol = url.protocol.replace("http", "ws");
    url.searchParams.set("ticket", ticket);

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.backoff = 500;
      ws.send(JSON.stringify({ type: "hello", since: this.store.seq }));
      // 切れている間に打ったものを送り直す。操作 id が同じなので二重には入らない。
      const unsent = this.store.unsent();
      if (unsent.length) ws.send(JSON.stringify({ type: "ops", ops: unsent }));
    });

    ws.addEventListener("message", (e) => {
      const msg = JSON.parse(e.data as string);
      if (msg.type === "presence") {
        // 前に居た場所から外し、新しい場所に置く。null なら画面から離れた合図。
        for (const [row, who] of this.presence) if (who === msg.actor) this.presence.delete(row);
        if (msg.at) this.presence.set(msg.at, msg.actor);
        this.onPresence?.();
        return;
      }
      if (msg.type === "ops" || msg.type === "ack") this.store.remote(msg.envelopes);
      // 追いつき終わりの合図。届いた操作が 0 件でも、ここまで読んだ印は進める。
      else if (msg.type === "synced") this.store.seq = Math.max(this.store.seq, msg.seq);
      this.onState("live", this.store.pendingCount ? `未送信 ${this.store.pendingCount} 件` : "接続中");
    });

    ws.addEventListener("close", () => this.retry("切断"));
    ws.addEventListener("error", () => ws.close());
  }

  private retry(reason: string): void {
    if (this.closed) return;
    this.onState("down", `${reason} · 繋ぎ直します`);
    setTimeout(() => this.connect(), this.backoff);
    this.backoff = Math.min(this.backoff * 2, 10_000);
  }
}
